import { useEffect } from 'react';

import { useMeQuery } from '@/features/auth';
import { env } from '@/shared/config/env';
import {
  clearTokens,
  getPersistedUserSnapshot,
  hasTokens,
  subscribeToTokenChanges,
  useSessionStore,
} from '@/shared/lib/auth';

/**
 * App-boot session hydration.
 *
 * When `VITE_AUTH_PROFILE_ENABLED` is true, validates tokens via `GET` profile.
 * When false, relies on the user snapshot written at login + `hydrateUserSnapshotFromStorage`.
 */
export function useSessionBootstrap(): void {
  const status = useSessionStore((s) => s.status);
  const markAnonymous = useSessionStore((s) => s.markAnonymous);
  const clear = useSessionStore((s) => s.clear);

  const tokensPresent = hasTokens();
  const profileQueryEnabled = tokensPresent && env.authProfileEnabled;

  const meQuery = useMeQuery(profileQueryEnabled);

  useEffect(() => {
    if (!tokensPresent && status === 'unknown') {
      markAnonymous();
    }
  }, [markAnonymous, status, tokensPresent]);

  useEffect(() => {
    if (tokensPresent && !env.authProfileEnabled && !getPersistedUserSnapshot()) {
      clearTokens();
      markAnonymous();
    }
  }, [markAnonymous, tokensPresent]);

  useEffect(() => {
    if (meQuery.isError && status === 'unknown') {
      markAnonymous();
    }
  }, [markAnonymous, meQuery.isError, status]);

  useEffect(() => {
    if (!env.authProfileEnabled) return;
    if (meQuery.isError && tokensPresent) {
      clearTokens();
      clear();
    }
  }, [clear, meQuery.isError, tokensPresent]);

  useEffect(() => {
    const unsub = subscribeToTokenChanges((access) => {
      if (!access) clear();
    });
    return unsub;
  }, [clear]);
}
